import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, FileText, Clock, CheckCircle, XCircle, ArrowRight, Shield, Sparkles, Globe, Calendar, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { trackingAPI } from '../lib/api';
import { useTheme } from '../App';

const STATUS_CONFIG = {
  pending_payment: { icon: Clock, color: '#F59E0B', label: 'Pending Payment', glow: 'rgba(245,158,11,0.3)' },
  submitted: { icon: FileText, color: '#3B82F6', label: 'Submitted', glow: 'rgba(59,130,246,0.3)' },
  in_review: { icon: Shield, color: '#8B5CF6', label: 'In Review', glow: 'rgba(139,92,246,0.3)' },
  approved: { icon: CheckCircle, color: '#10B981', label: 'Approved', glow: 'rgba(16,185,129,0.3)' },
  rejected: { icon: XCircle, color: '#EF4444', label: 'Rejected', glow: 'rgba(239,68,68,0.3)' },
  completed: { icon: CheckCircle, color: '#06B6D4', label: 'Completed', glow: 'rgba(6,182,212,0.3)' },
};

const PARTICLES = Array.from({ length: 20 }, (_, i) => ({
  id: i,
  size: Math.random() * 4 + 2,
  x: Math.random() * 100,
  y: Math.random() * 100,
  delay: Math.random() * 5,
  duration: Math.random() * 4 + 6,
}));

export default function TrackingPage() {
  const { t } = useTranslation();
  const { code: urlCode } = useParams();
  const { theme } = useTheme();
  const isDark = theme !== 'aurora';
  const [code, setCode] = useState(urlCode || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (urlCode) {
      setCode(urlCode.toUpperCase());
      handleTrack(urlCode);
    }
  // eslint-disable-next-line
  }, [urlCode]);

  const handleTrack = async (c) => {
    const trackCode = (c || code).trim().toUpperCase();
    if (!trackCode) return;
    setLoading(true); setNotFound(false); setResult(null);
    try {
      const r = await trackingAPI.track(trackCode);
      setResult(r.data);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const statusSteps = ['pending_payment', 'submitted', 'in_review', 'approved'];
  const idx = result ? statusSteps.indexOf(result.status) : -1;
  const currentStatus = result ? STATUS_CONFIG[result.status] || STATUS_CONFIG.submitted : null;

  const textP = isDark ? 'text-white' : 'text-gray-900';
  const textS = isDark ? 'text-white/60' : 'text-gray-500';
  const textM = isDark ? 'text-white/40' : 'text-gray-400';

  return (
    <div className={`min-h-screen relative overflow-hidden transition-colors ${isDark ? '' : 'bg-[#F8FAFC]'}`} data-testid="tracking-page">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />

      {/* Floating Particles */}
      {isDark && PARTICLES.map(p => (
        <motion.div
          key={p.id}
          className="particle"
          style={{
            width: p.size, height: p.size, left: `${p.x}%`, top: `${p.y}%`,
            background: `radial-gradient(circle, rgba(59,130,246,0.6), transparent)`,
          }}
          animate={{ y: [0, -30, 0], x: [0, 10, -10, 0], opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}

      {/* Center Glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{ background: isDark ? 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, rgba(139,92,246,0.04) 30%, transparent 70%)' : 'radial-gradient(circle, rgba(59,130,246,0.04) 0%, transparent 60%)' }} />

      <div className="relative flex flex-col items-center justify-center min-h-screen px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
          className="w-full max-w-xl"
        >
          {/* Header */}
          <div className="text-center mb-10">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.6, delay: 0.2, type: 'spring', stiffness: 200 }}
              className="inline-block mb-6"
            >
              <div className={`relative w-20 h-20 rounded-3xl flex items-center justify-center ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border-2 border-blue-200'}`}
                style={{ boxShadow: isDark ? '0 0 40px rgba(59,130,246,0.15), inset 0 0 30px rgba(59,130,246,0.05)' : '' }}>
                <Search className="w-9 h-9 text-blue-500" />
                <motion.div
                  className="absolute inset-0 rounded-3xl border border-blue-500/30"
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
              </div>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className={`text-4xl md:text-5xl font-extrabold tracking-tight ${textP}`}
              style={{ fontFamily: 'Outfit' }}
            >
              {t('tracking.title')}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className={`mt-3 text-base ${textM}`}
            >
              {t('tracking.enterCode')}
            </motion.p>
          </div>

          {/* Search Box - 3D Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="card-3d mb-10"
          >
            <div className={`card-3d-inner rounded-2xl p-6 border-2 transition-all ${
              isDark
                ? 'bg-[#0A0A0C]/80 backdrop-blur-2xl border-white/[0.08]'
                : 'bg-white/90 backdrop-blur-xl border-gray-200 shadow-2xl shadow-gray-200/50'
            }`}
              style={{ boxShadow: isDark ? '0 0 60px rgba(59,130,246,0.06), 0 20px 80px rgba(0,0,0,0.4)' : '' }}
            >
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && handleTrack()}
                    className={`h-16 text-center text-3xl tracking-[0.3em] font-mono rounded-xl border-2 transition-all ${
                      isDark
                        ? 'bg-white/[0.03] border-white/[0.08] text-white focus:border-blue-500/40 focus:bg-white/[0.05]'
                        : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-400 focus:bg-white'
                    }`}
                    placeholder="XXXXXXXX"
                    maxLength={8}
                    data-testid="tracking-code-input"
                  />
                </div>
                <Button
                  onClick={() => handleTrack()}
                  disabled={loading || !code.trim()}
                  className="h-16 w-16 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
                  data-testid="track-btn"
                >
                  {loading ? (
                    <RefreshCw className="w-6 h-6 animate-spin" />
                  ) : (
                    <ArrowRight className="w-6 h-6" />
                  )}
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Not Found */}
          <AnimatePresence>
            {notFound && (
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                className={`text-center p-8 rounded-2xl border-2 mb-8 ${isDark ? 'bg-red-500/5 border-red-500/20' : 'bg-red-50 border-red-200'}`}
                style={{ boxShadow: isDark ? '0 0 40px rgba(239,68,68,0.08)' : '' }}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                </motion.div>
                <p className="text-red-500 font-semibold text-lg">Appeal not found</p>
                <p className={`text-sm mt-1 ${textM}`}>Please check your tracking code and try again</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Result */}
          <AnimatePresence>
            {result && (
              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
                data-testid="tracking-result"
              >
                {/* Status Header Card */}
                <div
                  className={`rounded-2xl p-6 border-2 mb-6 relative overflow-hidden holographic holo-shine ${
                    isDark ? 'bg-[#0A0A0C]/80 backdrop-blur-2xl border-white/[0.08]' : 'bg-white border-gray-200 shadow-xl'
                  }`}
                  style={{ boxShadow: isDark ? `0 0 40px ${currentStatus?.glow || 'rgba(59,130,246,0.1)'}` : '' }}
                >
                  <div className="flex items-center justify-between relative z-10">
                    <div>
                      <p className={`text-xs uppercase tracking-widest mb-1 ${textM}`}>Tracking Code</p>
                      <p className={`text-3xl font-mono font-extrabold tracking-wider ${textP}`}>
                        {result.tracking_code}
                      </p>
                    </div>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.3, type: 'spring' }}
                      className="w-16 h-16 rounded-2xl flex items-center justify-center timeline-node-active"
                      style={{
                        background: `${currentStatus?.color}15`,
                        border: `2px solid ${currentStatus?.color}40`,
                        '--status-color': currentStatus?.glow,
                      }}
                    >
                      {currentStatus && <currentStatus.icon className="w-8 h-8" style={{ color: currentStatus.color }} />}
                    </motion.div>
                  </div>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {[
                    { icon: Globe, label: 'Platform', value: result.platform },
                    { icon: Shield, label: 'Issue Type', value: result.issue_type },
                    { icon: Calendar, label: t('tracking.created'), value: new Date(result.created_at).toLocaleDateString() },
                    { icon: Clock, label: t('tracking.updated'), value: new Date(result.updated_at).toLocaleDateString() },
                  ].map((item, i) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + i * 0.1 }}
                      className="card-3d"
                    >
                      <div className={`card-3d-inner p-4 rounded-xl border ${
                        isDark ? 'bg-white/[0.02] border-white/[0.06]' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <item.icon className={`w-3.5 h-3.5 ${isDark ? 'text-blue-400/60' : 'text-blue-500/60'}`} />
                          <p className={`text-xs uppercase tracking-wider ${textM}`}>{item.label}</p>
                        </div>
                        <p className={`text-sm font-semibold ${textP}`}>{item.value}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* 3D Status Timeline */}
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className={`rounded-2xl p-6 border ${
                    isDark ? 'bg-[#0A0A0C]/60 backdrop-blur-xl border-white/[0.06]' : 'bg-white border-gray-200 shadow-lg'
                  }`}
                >
                  <p className={`text-xs uppercase tracking-widest mb-6 ${textM}`}>
                    <Sparkles className="w-3 h-3 inline mr-1" />
                    {t('tracking.status')} Progress
                  </p>

                  <div className="flex items-center justify-between relative">
                    {/* Connection Line */}
                    <div className={`absolute top-5 left-[10%] right-[10%] h-0.5 ${isDark ? 'bg-white/[0.06]' : 'bg-gray-200'}`}>
                      <motion.div
                        className="h-full bg-blue-500 rounded-full"
                        initial={{ width: '0%' }}
                        animate={{ width: idx >= 0 ? `${(idx / (statusSteps.length - 1)) * 100}%` : '0%' }}
                        transition={{ duration: 1.5, delay: 0.5, ease: [0.23, 1, 0.32, 1] }}
                        style={{ boxShadow: '0 0 10px rgba(59,130,246,0.4)' }}
                      />
                    </div>

                    {statusSteps.map((s, i) => {
                      const config = STATUS_CONFIG[s];
                      const isActive = i <= idx;
                      const isCurrent = i === idx;
                      const isRejected = result.status === 'rejected' && isCurrent;

                      return (
                        <motion.div
                          key={s}
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.6 + i * 0.15, type: 'spring', stiffness: 300 }}
                          className="flex flex-col items-center gap-2 relative z-10"
                          style={{ width: `${100 / statusSteps.length}%` }}
                        >
                          <div
                            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                              isCurrent ? 'timeline-node-active' : ''
                            }`}
                            style={{
                              background: isActive
                                ? isRejected ? 'rgba(239,68,68,0.15)' : `${config.color}15`
                                : isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                              border: `2px solid ${isActive ? (isRejected ? 'rgba(239,68,68,0.4)' : `${config.color}40`) : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}`,
                              '--status-color': config.glow,
                              boxShadow: isActive ? `0 0 20px ${config.glow}` : 'none',
                            }}
                          >
                            <config.icon
                              className="w-4 h-4"
                              style={{ color: isActive ? (isRejected ? '#EF4444' : config.color) : isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)' }}
                            />
                          </div>
                          <span className={`text-[10px] font-medium capitalize text-center leading-tight ${
                            isActive ? textS : textM
                          }`}>
                            {s.replace('_', ' ')}
                          </span>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>

                {/* Current Status Banner */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 }}
                  className="mt-6 rounded-xl p-4 border-2 text-center"
                  style={{
                    background: `${currentStatus?.color}08`,
                    borderColor: `${currentStatus?.color}25`,
                    boxShadow: isDark ? `0 0 30px ${currentStatus?.glow}` : '',
                  }}
                >
                  <div className="flex items-center justify-center gap-2">
                    {currentStatus && <currentStatus.icon className="w-5 h-5" style={{ color: currentStatus.color }} />}
                    <span className="font-semibold capitalize" style={{ color: currentStatus?.color }}>
                      {currentStatus?.label || result.status?.replace('_', ' ')}
                    </span>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
